import React, { FC, useEffect, useMemo } from 'react';
import { createPictureFactory } from '@funtech-int/yango-shared';
import { PromoBlock } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createSignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';
import classnames from 'classnames';

import { RouterLink } from '@/shared/components/RouterLink/RouterLink';

import {
    MoviePromoContentActions,
    MoviePromoContentActionsAnalytics,
} from '../MoviePromoContentActions/MoviePromoContentActions';
import { filmRoute } from '../../routes';
import { BackgroundTrailer } from '../BackgroundTrailer/BackgroundTrailer';

import styles from './MoviePromoBlock.module.css';

const logoFactory = createPictureFactory({
    fallback: {
        src: '960x540',
    },
});

const badgeFactory = createPictureFactory({
    fallback: {
        src: '210x120',
    },
});

interface MoviePromoBlockAnalytics extends MoviePromoContentActionsAnalytics {
    itemImpressionShowed(this: void): void;
    trailerPlayerPreviewStarted(this: void): void;
    trailerPlayerPreviewFinished(this: void): void;
}

export interface MoviePromoBlockProps {
    promoBlock: PromoBlock;
    onDislike: () => void;
    analytics?: MoviePromoBlockAnalytics;
}

export const MoviePromoBlock: FC<MoviePromoBlockProps> = ({ promoBlock, onDislike, analytics }) => {
    const { img: logoImg } = logoFactory({ fallbackUrl: promoBlock.logo?.toString() });
    const { img: badgeImg } = badgeFactory({ fallbackUrl: promoBlock.badge?.toString() });

    const controller = useMemo(() => {
        const ready = createSignal(false);
        const trailerStarted = createSignal(false);

        return {
            ready: ready.seal(),
            trailerStarted: trailerStarted.seal(),
            onContentActionsMount() {
                ready.set(true);
            },
            onLinkClick() {
                analytics?.itemImpressionNavigated('movie_card');
            },
            onTrailerStart() {
                analytics?.trailerPlayerPreviewStarted();
                trailerStarted.set(true);
            },
            onTrailerEnd() {
                analytics?.trailerPlayerPreviewFinished();
                trailerStarted.set(false);
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promoBlock, analytics]);

    useEffect(() => {
        analytics?.itemImpressionShowed();
    }, [promoBlock, analytics]);

    const ready = useSignal(controller.ready);
    const trailerStarted = useSignal(controller.trailerStarted);

    return (
        <section className={styles.root}>
            <div className={styles.background}>
                <BackgroundTrailer
                    contentId={promoBlock.videoContentId ?? undefined}
                    backgroundUrl={promoBlock.cover?.toString()}
                    title={promoBlock.title.toString()}
                    buttonSize="xxl"
                    onTrailerStart={controller.onTrailerStart}
                    onTrailerEnd={controller.onTrailerEnd}
                />
                <div
                    className={classnames(styles.gradient, {
                        [styles.trailerStarted]: trailerStarted,
                    })}
                />
                {badgeImg.src && <img src={badgeImg.src} alt="" className={styles.badge} />}
            </div>
            <RouterLink
                to={filmRoute({ id: promoBlock.contentId })}
                onClick={controller.onLinkClick}
                className={styles.link}
            />
            <div
                className={classnames(styles.content, {
                    [styles.active]: ready,
                })}
            >
                <h1>
                    <img
                        src={logoImg.src}
                        key={logoImg.src}
                        alt={promoBlock.title.toString()}
                        className={styles.logo}
                    />
                </h1>
                <p className={styles.description}>{promoBlock.shortDescription}</p>
            </div>
            <div
                className={classnames(styles.actions, {
                    [styles.active]: ready,
                })}
            >
                <MoviePromoContentActions
                    contentId={promoBlock.contentId}
                    onDislike={onDislike}
                    onMount={controller.onContentActionsMount}
                    analytics={analytics}
                />
            </div>
        </section>
    );
};
